/**
 * EnemyDatabase — enemy type definitions and encounter generation.
 *
 * Enemy base stats are fixed per type. Level is derived from map depth
 * (0 = start area, 1 = boss area) and adds per-level stat bonuses.
 *
 * Spatial modifiers (like alert status) are applied after creation
 * via the status effect system on GameCharacter.
 */
import { EnemyDef, EnemyCharacter } from "./EnemyCharacter";

const ENEMIES: Record<string, EnemyDef> = {
  slime: {
    name: "Slime",
    maxHp: 8,
    attack: 2,
    armor: 0,
    goldReward: 5,
    tier: "normal",
  },
  goblin_elite: {
    name: "Goblin Chief",
    maxHp: 16,
    attack: 3,
    armor: 2,
    goldReward: 12,
    tier: "elite",
  },
  dragon_boss: {
    name: "Dragon",
    maxHp: 30,
    attack: 5,
    armor: 4,
    goldReward: 25,
    tier: "boss",
  },
};

/**
 * Derive enemy level from map depth (0–1).
 * Early nodes: level 1–2, mid: 2–3, late: 3–5.
 * Adds ±1 random jitter (clamped to 1–5).
 */
export function enemyLevel(depth: number): number {
  // Base level from depth: 1 at depth 0, 5 at depth 1
  const base = 1 + depth * 4;
  // Random jitter ±1
  const jitter = Math.random() < 0.5 ? -1 : (Math.random() < 0.5 ? 0 : 1);
  return Math.max(1, Math.min(5, Math.round(base + jitter)));
}

/**
 * Generate the enemy group for a battle encounter.
 * @param depth — map progress 0 (start) to 1 (boss area)
 */
export function getEncounter(isBoss: boolean, isElite: boolean, depth: number = 0): EnemyCharacter[] {
  if (isBoss) {
    return [new EnemyCharacter(ENEMIES.dragon_boss, 5)];
  }

  const level = enemyLevel(depth);

  if (isElite) {
    return [new EnemyCharacter(ENEMIES.goblin_elite, level)];
  }

  // Normal battles: randomly 1 or 2 slimes
  const count = Math.random() < 0.5 ? 1 : 2;
  return Array.from({ length: count }, () => new EnemyCharacter(ENEMIES.slime, level));
}
